import { useCallback, useEffect, useState, useRef } from "react";
import { DeleteTwoTone, EditTwoTone } from "@ant-design/icons";
import {
  Button,
  message,
  Row,
  Col,
  Dropdown,
  Menu,
  Popconfirm,
  Modal,
  Form,
  Input,
  Switch,
  Typography,
} from "antd";
import ListTable from "../../../common/ListTable";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ExtraContent from "../CommonComponents/ExtraContent";
import FilterComponent from "../CommonComponents/FilterComponent";
import { getData, postData, deleteData, putData } from "../../../apiServices/common";
import {
  PACKING_LINES_ENDPOINT,
  PACKING_LINES_ADD_ENDPOINT,
  PACKING_LINES_EDIT_ENDPOINT,
  PACKING_LINES_DELETE_ENDPOINT,
} from "../../../apiServices/API_ENDPOINTS";
import { useSelector } from "react-redux";
import { hasPermission } from "../../../utils/functions";
import {
  PACKING_LINE_CREATE_PERMISSION,
  PACKING_LINE_EDIT_PERMISSION,
  PACKING_LINE_DELETE_PERMISSION,
} from "../../../routes/permissions";
import { useLocation } from "react-router";

const PackingLineConfig = () => {
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [warehouseForm] = Form.useForm();
  const { Title } = Typography;

  const filterData = useSelector(({ filter }) => filter.filterData);
  const prevFilterDataRef = useRef(filterData);
  const currentPageRef = useRef(currentPage);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllPackingLines = useCallback(async (filterValues, page = null) => {
    const { name } = filterValues || {};
    setLoading(true);
    setFetchError(null);

    let pageToUse;
    if (page !== null) {
      pageToUse = page;
    } else if (filterValues !== null && filterValues !== undefined) {
      // When filterValues is provided (search/filter operation), always use page 1
      pageToUse = 1;
      setCurrentPage(1);
      currentPageRef.current = 1;
    } else {
      pageToUse = currentPageRef.current;
    }

    const query = `${PACKING_LINES_ENDPOINT}?page=${pageToUse}`;
    const bodyData = {
      ...(name && { name: name }),
    };

    const response = await getData(query, false, bodyData);
    if (response?.status === 200) {
      setLoading(false);
      const pageFromResponse = response?.data?.data?.current_page;
      setCurrentPage(pageFromResponse);
      currentPageRef.current = pageFromResponse;
      setMetaData({
        defaultPageSize: response?.data?.data?.per_page,
        current: pageFromResponse,
        defaultCurrent: pageFromResponse,
        total: response?.data?.data?.total,
      });
      setTableData(response?.data?.data?.data);
    } else {
      setLoading(false);
      setFetchError("Could not load this list.");
      message.error("Error getting garments parts data");
    }
  }, []);

  useEffect(() => {
    const isFilterChanged = prevFilterDataRef.current !== filterData && prevFilterDataRef.current !== null;

    if (isFilterChanged) {
      setCurrentPage(1);
      currentPageRef.current = 1;

      prevFilterDataRef.current = filterData;

      getAllPackingLines(filterData, 1);
    } else {
      prevFilterDataRef.current = filterData;

      getAllPackingLines(filterData, currentPageRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, filterData]);

  const onModalOpen = () => {
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    warehouseForm.resetFields();
    setIsModalOpen(false);
    setIsEdit(false);
  };

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const deleteSelectedPackingLine = async (id) => {
    setLoading(true);
    const response = await deleteData(`${PACKING_LINES_DELETE_ENDPOINT}/${id}`);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Packing Line Deleted Successfully");
      refetch();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const editSelectedPackingLine = async (formValues) => {
    setLoading(true);

    const bodyData = {
      name: formValues?.name,
      description: formValues?.description || "",
      status: formValues?.status ? "active" : "inactive",
    };

    const response = await putData(`${PACKING_LINES_EDIT_ENDPOINT}/${formValues?.id}`, bodyData);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Packing Line Edited Successfully");
      refetch();
      warehouseForm.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const createNewPackingLine = async (formValues) => {
    setLoading(true);

    const bodyData = {
      name: formValues?.name,
      description: formValues?.description || "",
      status: formValues?.status ? "active" : "inactive",
    };

    const response = await postData(PACKING_LINES_ADD_ENDPOINT, bodyData);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Packing Line Created Successfully");
      refetch();
      warehouseForm.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const openModalForEdit = async (record) => {
    setIsEdit(true);
    setLoading(true);
    setIsModalOpen(true);

    try {
      const response = await getData(`${PACKING_LINES_ENDPOINT}/${record?.id}/edit`);
      console.log(response);

      if (response?.data?.code === 200) {
        const data = response?.data?.data;
        setLoading(false);
        warehouseForm.setFieldsValue({
          id: data?.id,
          name: data?.name,
          description: data?.description || "",
          status: data?.status === "active" ? true : false,
        });
      } else {
        setLoading(false);
        message.error("Error fetching packing line data");
        setIsModalOpen(false);
        setIsEdit(false);
      }
    } catch (error) {
      setLoading(false);
      message.error("Error fetching packing line data");
      setIsModalOpen(false);
      setIsEdit(false);
    }
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    getAllPackingLines(filterData, newPage);
  };

  const tableColumns = [
    {
      title: "Name",
      dataIndex: ["name"],
      key: "name",
    },
    {
      title: "Description",
      dataIndex: ["description"],
      key: "description",
      render: (_, record) => {
        return <div>{record?.description || "-"}</div>;
      },
    },
    {
      title: "Status",
      dataIndex: ["status"],
      key: "status",
      render: (_, record) => {
        return <div>{record?.status === "active" ? "Active" : "Inactive"}</div>;
      },
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Dropdown.Button
            overlay={
              <Menu
                items={[
                  {
                    label: (
                      <Popconfirm
                        title="Are you sure you want to delete?"
                        onConfirm={() => {
                          deleteSelectedPackingLine(record?.id);
                        }}
                      >
                        <span>
                          <DeleteTwoTone />
                        </span>
                        <span
                          style={{
                            color: "red",
                            marginLeft: 5,
                          }}
                        >
                          Delete
                        </span>
                      </Popconfirm>
                    ),
                    key: "1",
                    disabled: !hasPermission([PACKING_LINE_DELETE_PERMISSION]),
                  },
                  {
                    label: (
                      <div
                        onClick={() => {
                          openModalForEdit(record);
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <span>
                          <EditTwoTone />
                        </span>
                        <span style={{ marginLeft: 5 }}>Edit</span>
                      </div>
                    ),
                    key: "2",
                    disabled: !hasPermission([PACKING_LINE_EDIT_PERMISSION]),
                  },
                ]}
              />
            }
          ></Dropdown.Button>
        );
      },
    },
  ];

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={onModalOpen}
                disabled={!hasPermission([PACKING_LINE_CREATE_PERMISSION])}
                addButtonText={hasPermission([PACKING_LINE_CREATE_PERMISSION]) ? "Add New" : "No Permission"}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Packing Line Configuration</Title>}
          />
        }
        content={
          <>
            <FilterComponent
              tableData={tableData}
              filterAPI={getAllPackingLines}
              showFilter={showFilter}
              setShowFilter={setShowFilter}
            />

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllPackingLines(filterData, currentPageRef.current)}
                emptyText="No records to show yet."
                dataSource={tableData}
                columns={tableColumns}
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: (newPage) => {
                    changePage(newPage);
                  },
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
              />
            </div>
          </>
        }
      />
      <Modal
        open={isModalOpen}
        title={isEdit ? "Edit Packing Line" : "Create Packing Line"}
        onCancel={() => {
          onCloseModal();
        }}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        footer={null}
      >
        <Form form={warehouseForm} layout="vertical" onFinish={isEdit ? editSelectedPackingLine : createNewPackingLine}>
          <Row gutter={6}>
            <Col span={12}>
              <Form.Item name="id" hidden />
              <Form.Item
                name="name"
                placeholder="Name"
                label="Name"
                rules={[
                  {
                    required: true,
                    message: "Name Required",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                placeholder="Status"
                label="Status"
                rules={[
                  {
                    required: true,
                    message: "Status Required",
                  },
                ]}
                valuePropName="checked"
                initialValue={true}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={6}>
            <Col span={24}>
              <Form.Item name="description" placeholder="Description" label="Description">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={6}>
            <Col span={12}>
              <Button
                type="danger"
                style={{ width: "100%" }}
                onClick={() => {
                  onCloseModal();
                }}
              >
                Discard
              </Button>
            </Col>
            <Col span={12}>
              <Button type="primary" htmlType="submit" style={{ width: "100%" }} loading={loading}>
                Save
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default PackingLineConfig;
